/**
 * What a session is using now, from its transcript alone (ADR 0057 decision 2, build step 1):
 * cache writes, when the main thread last called the API, how long its prompt cache lives,
 * and how full its context window is. Both agents send it as `Session.ext.usage`; the
 * drawer's Usage group reads it with the helpers below. No mod is needed.
 */
import type { Session, TokenUsage, Ts } from '../index.ts';
import type { ModelPrice, PriceRates, TokenCounts } from './costs.ts';

/** The key under Session.ext. */
export const SESSION_USAGE_EXT = 'usage';

/** Claude Code's prompt cache lifetimes: 5 minutes by default, 1 hour for `ephemeral_1h` writes. */
export const CACHE_TTL_5M_MS = 300_000;
export const CACHE_TTL_1H_MS = 3_600_000;

export interface SessionLiveUsage {
  /** Tokens written to the prompt cache (every model, subagents included): 5 minute plus 1 hour. */
  cacheWrite: number;
  cacheWrite5m: number;
  cacheWrite1h: number;
  /** When the main thread's last API request answered (its last assistant message with usage). */
  lastRequestAt: Ts | null;
  /**
   * How long the cache lives after that request: 1 hour when the newest request that wrote to
   * the cache wrote to the 1 hour cache, else 5 minutes.
   */
  cacheTtlMs: number;
  /** The last request's prompt: input plus cache read plus cache write tokens. */
  contextTokens: number;
  /** The model's context window in tokens, or null when the model is not one Orgus knows. */
  contextWindow: number | null;
  /** Spend signals for the Watch (ADR 0062 decision 3), from the main thread. Older agents omit them. */
  coldResumes?: ColdResumes | null;
  progress?: SpendProgress;
  loop?: FailureLoop | null;
}

/**
 * Requests that rewrote the prompt cache after it went cold (the session resumed past its cache
 * lifetime and wrote at least half its prompt again), on one local day ('YYYY-MM-DD').
 */
export interface ColdResumes {
  day: string;
  count: number;
  /** Tokens those requests wrote: 5 minute plus 1 hour, and the 1 hour part. */
  cacheWrite: number;
  cacheWrite1h: number;
}

/** What counts as progress for the money: a commit, a passing test run, a landing. */
export type ProgressKind = 'commit' | 'test' | 'land';

/** The main thread's last progress, and its tokens since (per model key), or since it started. */
export interface SpendProgress {
  at: Ts | null;
  kind: ProgressKind | null;
  since: Record<string, TokenCounts>;
}

/** The same failing command or error, repeated: how often, and the main thread's tokens since the first. */
export interface FailureLoop {
  /** The command, or the tool and its error, one line. */
  what: string;
  count: number;
  firstAt: Ts | null;
  lastAt: Ts | null;
  since: Record<string, TokenCounts>;
}

/** What liveUsageOf passes through: the spend signals an agent tracks. */
export interface SpendSignals {
  coldResumes: ColdResumes | null;
  progress: SpendProgress;
  loop: FailureLoop | null;
}

/** Models with a 1M token context window (Anthropic's model table, checked 18 Oct 2026). */
const WINDOW_1M = [
  'claude-fable-5-1',
  'claude-mythos-5-1',
  'claude-fable-5',
  'claude-mythos-5',
  'claude-opus-5-5',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-opus-4-7',
  'claude-opus-4-6',
  'claude-sonnet-5-5',
  'claude-sonnet-5',
  'claude-sonnet-4-6',
  'claude-haiku-5-5',
];
/** Models with a 200K window (unless run with the 1M option, '[1m]'). */
const WINDOW_200K = ['claude-haiku-4-5', 'claude-sonnet-4-5', 'claude-sonnet-4', 'claude-opus-4-5', 'claude-opus-4-1', 'claude-opus-4', 'claude-3'];

/** Every Claude model id Orgus knows (the windows above), for the launch model check (ADR 0059). */
export const KNOWN_CLAUDE_MODELS: readonly string[] = [...WINDOW_1M, ...WINDOW_200K.filter((m) => m !== 'claude-3'), 'claude-haiku-3-5'];

/** 'us.anthropic.claude-sonnet-4-5-20250929-v1:0' and 'claude-opus-4-5@20251101' become 'claude-...' ids. */
export function normalizeModelId(model: string): string {
  const m = model.toLowerCase();
  const i = m.indexOf('claude-');
  return i >= 0 ? m.slice(i) : m;
}

/** Longest prefix of `prefixes` that `model` starts with on a word boundary (claude-opus-4 never matches claude-opus-4x). */
export function matchModelPrefix(model: string, prefixes: readonly string[]): string | null {
  const id = normalizeModelId(model);
  let best: string | null = null;
  for (const p of prefixes) {
    if (!id.startsWith(p)) continue;
    const next = id.charAt(p.length);
    if (next && /[0-9a-z]/.test(next)) continue;
    if (!best || p.length > best.length) best = p;
  }
  return best;
}

/**
 * A model's context window: 1M for a '[1m]' id or a 1M model, 200K for an older one, null when
 * unknown. A prompt larger than the window found means the session runs with the 1M option.
 */
export function contextWindowOf(model: string | null | undefined, contextTokens = 0): number | null {
  if (!model) return null;
  if (/\[1m\]/i.test(model)) return 1_000_000;
  const w = matchModelPrefix(model, WINDOW_1M) ? 1_000_000 : matchModelPrefix(model, WINDOW_200K) ? 200_000 : null;
  return w !== null && contextTokens > w ? 1_000_000 : w;
}

/** The last API request of a session's main thread, as an agent tracks it while reading the transcript. */
export interface LastRequest {
  at: Ts | null;
  input: number;
  cacheRead: number;
  cacheWrite: number;
  cacheWrite1h: number;
}

/** Build Session.ext.usage from the main thread's last request and the session's per-model counts. Null with no request yet. */
export function liveUsageOf(last: LastRequest | null, lastWrite1h: boolean, model: string | null, byModel: Record<string, TokenCounts> | undefined, spend?: SpendSignals): SessionLiveUsage | null {
  if (!last) return null;
  let cacheWrite = 0;
  let cacheWrite1h = 0;
  for (const c of Object.values(byModel ?? {})) {
    cacheWrite += c.cacheWrite;
    cacheWrite1h += c.cacheWrite1h;
  }
  const contextTokens = last.input + last.cacheRead + last.cacheWrite;
  return {
    cacheWrite,
    cacheWrite5m: Math.max(0, cacheWrite - cacheWrite1h),
    cacheWrite1h,
    lastRequestAt: last.at,
    cacheTtlMs: lastWrite1h ? CACHE_TTL_1H_MS : CACHE_TTL_5M_MS,
    contextTokens,
    contextWindow: contextWindowOf(model, contextTokens),
    ...(spend ? { coldResumes: spend.coldResumes, progress: spend.progress, loop: spend.loop } : {}),
  };
}

/** Session.ext.usage, when the agent sent it. */
export function sessionLiveUsage(s: Pick<Session, 'ext'> | null | undefined): SessionLiveUsage | null {
  const u = s?.ext?.[SESSION_USAGE_EXT] as Partial<SessionLiveUsage> | undefined;
  return u && typeof u === 'object' && typeof u.contextTokens === 'number' && typeof u.cacheTtlMs === 'number' ? (u as SessionLiveUsage) : null;
}

export type CacheWarmth = { state: 'warm'; leftMs: number } | { state: 'cold' } | { state: 'unknown' };

/** Is the prompt cache still warm at `now`, and for how long. */
export function cacheWarmth(u: Pick<SessionLiveUsage, 'lastRequestAt' | 'cacheTtlMs'> | null, now: number): CacheWarmth {
  if (!u || u.lastRequestAt === null) return { state: 'unknown' };
  const left = u.lastRequestAt + u.cacheTtlMs - now;
  return left > 0 ? { state: 'warm', leftMs: left } : { state: 'cold' };
}

/** Share of all input tokens read from the cache (0 to 1), or null with no input yet. `tokens.input` already counts cache writes. */
export function cacheHitRate(tokens: TokenUsage): number | null {
  const all = tokens.input + tokens.cacheRead;
  return all > 0 ? tokens.cacheRead / all : null;
}

/** Rates for a usage key ('<model>', '<model>#fast', '<model>#long'), longest match on the price list. */
export function ratesForKey(key: string, prices: readonly ModelPrice[]): PriceRates | null {
  const i = key.indexOf('#');
  const model = i < 0 ? key : key.slice(0, i);
  const variant = i < 0 ? null : key.slice(i + 1);
  const match = matchModelPrefix(model, prices.map((p) => p.match));
  const p = match ? prices.find((x) => x.match === match) : undefined;
  if (!p) return null;
  return (variant === 'fast' && p.fast) || (variant === 'long' && p.long) || p;
}

/**
 * What the cache saved, USD: every cache read token at the uncached input price less the cache
 * read price. Null when a model with cache reads has no price (a partial figure would mislead).
 */
export function cacheSavedUsd(byModel: Record<string, Pick<TokenCounts, 'cacheRead'>> | undefined, prices: readonly ModelPrice[] | undefined): number | null {
  if (!byModel || !prices?.length) return null;
  let saved = 0;
  for (const [key, c] of Object.entries(byModel)) {
    if (c.cacheRead <= 0) continue;
    const r = ratesForKey(key, prices);
    if (!r) return null;
    saved += (c.cacheRead * (r.input - r.cacheRead)) / 1e6;
  }
  return saved;
}
